import * as React from "react";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import CircularProgress from "@mui/material/CircularProgress";
import { selectUserToken } from "../../../../features/userSlice";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";
import {
  AdvisorGapRule,
  advisorCanonicalMetrics,
} from "../helpers/advisorStaticData";

type AdvisorOption = { id: number; label: string };

interface Props {
  open: boolean;
  /** the rule being edited, found by id from the list */
  gapRule: AdvisorGapRule | null;
  onClose: () => void;
  /** called after a rule is saved, so the list can be refreshed */
  onUpdated: () => void;
}

const AdvisorEditGapRule = ({ open, gapRule, onClose, onUpdated }: Props) => {
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const destinationTablesData = useSelector(
    (state: any) => state.advisor?.destinationTablesData,
  );

  const [submitting, setSubmitting] = React.useState(false);
  const [ruleTitle, setRuleTitle] = React.useState("");
  const [targetField, setTargetField] = React.useState<string | null>(null);
  // the payload needs the table's id, so the whole option is kept
  const [destinationTable, setDestinationTable] =
    React.useState<AdvisorOption | null>(null);
  const [expressionSql, setExpressionSql] = React.useState("");
  const [expressionJson, setExpressionJson] = React.useState("");
  const [simpleEnglish, setSimpleEnglish] = React.useState("");
  const [dependsOn, setDependsOn] = React.useState("");
  const [dataSource, setDataSource] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);

  const destinationTableOptions: AdvisorOption[] = React.useMemo(() => {
    const options: AdvisorOption[] =
      destinationTablesData?.map((item: any) => ({
        label: item?.destination_table,
        id: item?.id,
      })) || [];

    // a rule can point at a table the dropdown endpoint does not return, so
    // its own table is added rather than leaving the field blank on open
    if (
      gapRule?.destination_table_id &&
      !options.some((option) => option.id === gapRule.destination_table_id)
    ) {
      options.push({
        id: gapRule.destination_table_id,
        label:
          gapRule.destination_table || String(gapRule.destination_table_id),
      });
    }
    return options;
  }, [destinationTablesData, gapRule]);

  // same reasoning as the destination table - keep the rule's own value usable
  const targetFieldOptions: string[] = React.useMemo(() => {
    const options = [...advisorCanonicalMetrics];
    if (gapRule?.target_field && !options.includes(gapRule.target_field)) {
      options.push(gapRule.target_field);
    }
    return options;
  }, [gapRule]);

  // Seed the form from the rule each time the dialog is opened for one.
  React.useEffect(() => {
    if (!open || !gapRule) return;
    setRuleTitle(gapRule.rule_title ?? "");
    setTargetField(gapRule.target_field ?? null);
    setDestinationTable(
      destinationTableOptions.find(
        (option) => option.id === gapRule.destination_table_id,
      ) ?? null,
    );
    setExpressionSql(gapRule.expression_sql ?? "");
    setExpressionJson(
      gapRule.expression ? JSON.stringify(gapRule.expression) : "",
    );
    setSimpleEnglish(gapRule.description ?? "");
    setDependsOn(gapRule.dependency_field ?? "");
    setDataSource(gapRule.data_source ?? "");
    setIsActive(Boolean(gapRule.is_active));
  }, [open, gapRule, destinationTableOptions]);

  // typed by hand but sent as an object, so it has to parse before submitting
  const isExpressionJsonValid = React.useMemo(() => {
    if (!expressionJson.trim()) return true;
    try {
      JSON.parse(expressionJson);
      return true;
    } catch {
      return false;
    }
  }, [expressionJson]);

  const isValidPayload = Boolean(
    ruleTitle.trim() &&
    targetField &&
    destinationTable &&
    expressionSql.trim() &&
    simpleEnglish.trim() &&
    isExpressionJsonValid,
  );

  /**
   * Only the fields the user actually changed are sent, so an edit to one
   * field goes up as e.g. { "description": "Test 1" } rather than the whole
   * rule. Each value is normalised the same way on both sides before being
   * compared, so formatting alone never counts as a change.
   */
  const changedFields = React.useMemo(() => {
    const payload: Record<string, any> = {};
    if (!gapRule) return payload;
    const nextRuleTitle = ruleTitle.trim();
    if (nextRuleTitle !== (gapRule.rule_title ?? "")) {
      payload.rule_title = nextRuleTitle;
    }
    if (targetField !== (gapRule.target_field ?? null)) {
      payload.target_field = targetField;
    }
    const nextDestinationTableId = String(destinationTable?.id ?? "");
    if (nextDestinationTableId !== String(gapRule.destination_table_id ?? "")) {
      payload.destination_table_id = nextDestinationTableId;
    }
    const nextExpressionSql = expressionSql.trim();
    if (nextExpressionSql !== (gapRule.expression_sql ?? "")) {
      payload.expression_sql = nextExpressionSql;
    }
    if (isExpressionJsonValid) {
      const nextExpression = expressionJson.trim()
        ? JSON.parse(expressionJson)
        : null;
      if (
        JSON.stringify(nextExpression) !==
        JSON.stringify(gapRule.expression ?? null)
      ) {
        payload.expression = nextExpression;
      }
    }
    const nextDescription = simpleEnglish.trim();
    if (nextDescription !== (gapRule.description ?? "")) {
      payload.description = nextDescription;
    }
    const nextDependsOn = dependsOn.trim() || null;
    if (nextDependsOn !== (gapRule.dependency_field || null)) {
      payload.dependency_field = nextDependsOn;
    }
    const nextDataSource = dataSource.trim() || null;
    if (nextDataSource !== (gapRule.data_source || null)) {
      payload.data_source = nextDataSource;
    }
    if (isActive !== Boolean(gapRule.is_active)) {
      payload.is_active = isActive;
    }
    return payload;
  }, [
    gapRule,
    ruleTitle,
    targetField,
    destinationTable,
    expressionSql,
    expressionJson,
    isExpressionJsonValid,
    simpleEnglish,
    dependsOn,
    dataSource,
    isActive,
  ]);

  const hasChanges = Object.keys(changedFields).length > 0;

  const handleClose = () => {
    onClose();
  };

  const handleSaveChanges = async () => {
    if (!isValidPayload || !hasChanges || !gapRule) return;
    setSubmitting(true);
    try {
      const isSuccess = await AdvisorMapsEndpointLogic.updateGapRule(
        oauthTokenHeader,
        gapRule.id,
        changedFields,
      );
      if (isSuccess) {
        onUpdated();
        handleClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Edit Rule</DialogTitle>
      <Typography
        variant="body2"
        sx={{ px: 3, pb: 1, fontSize: "14px", color: "text.secondary" }}
      >
        A rule derives one metric on one destination. It is offered to maps only
        while active and matching that metric + destination.
      </Typography>

      <DialogContent>
        <Grid container spacing={2}>
          {/* only Simple English is editable here - the rest of the rule is
              shown for context but cannot be changed */}
          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Rule Title"
              placeholder="How this rule will be called"
              required
              disabled
              color="secondary"
              value={ruleTitle}
              onChange={(event) => setRuleTitle(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={targetFieldOptions}
              value={targetField}
              disabled
              onChange={(_, newValue) => setTargetField(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Field"
                  placeholder="Select a metric from the list"
                  required
                  color="secondary"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={destinationTableOptions}
              value={destinationTable}
              disabled
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => setDestinationTable(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination Table"
                  placeholder="Select a table from the list"
                  required
                  color="secondary"
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Expression (SQL)"
              placeholder="How this rule will be called"
              required
              disabled
              color="secondary"
              value={expressionSql}
              onChange={(event) => setExpressionSql(event.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Expression (JSON op-tree)"
              placeholder='{"op": "*", "args": ["qftd", "cpa_deal"]}'
              helperText={
                isExpressionJsonValid
                  ? "Optional - must be valid JSON"
                  : "Not valid JSON"
              }
              error={!isExpressionJsonValid}
              disabled
              color="secondary"
              multiline
              rows={2}
              value={expressionJson}
              onChange={(event) => setExpressionJson(event.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Simple English"
              placeholder="Filled automatically"
              required
              color="secondary"
              multiline
              rows={2}
              value={simpleEnglish}
              onChange={(event) => setSimpleEnglish(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {/* free text so several metrics can be given, comma separated */}
            <Autocomplete
              freeSolo
              options={advisorCanonicalMetrics}
              value={dependsOn}
              disabled
              onChange={(_, newValue) => setDependsOn(newValue ?? "")}
              onInputChange={(_, newValue) => setDependsOn(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Depends On"
                  placeholder="Select a metric from the list"
                  color="secondary"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* free text and optional - sent as null when left blank */}
            <TextField
              sx={{ width: "100%" }}
              label="Data Source"
              placeholder="Select a table from the list"
              disabled
              color="secondary"
              value={dataSource}
              onChange={(event) => setDataSource(event.target.value)}
            />
          </Grid>
        </Grid>

        <Typography
          sx={{ fontSize: "12px", color: "text.secondary", mt: 2, mb: 1 }}
        >
          Fields marked with * are mandatory.
        </Typography>

        <FormControlLabel
          disabled
          control={
            <Checkbox
              color="secondary"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
          }
          label="Set as active"
        />
      </DialogContent>

      <DialogActions sx={{ padding: "8px 24px 16px" }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Button color="secondary" disabled={submitting} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!isValidPayload || !hasChanges || submitting}
            onClick={handleSaveChanges}
            data-tracker="958"
            sx={{ whiteSpace: "nowrap" }}
          >
            Save Changes
            {submitting ? (
              <CircularProgress size={16} color="inherit" sx={{ ml: "8px" }} />
            ) : (
              <SaveIcon sx={{ width: "20px", ml: "5px" }} />
            )}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AdvisorEditGapRule;
